import React from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useFilterAutocompleteSearch } from "../../hooks/useFilterAutocompleteSearch";
import {
  FilterAutocompleteOption,
  hasAutocompleteConfig,
} from "../../utils/filterAutocompleteConfig";

interface AutocompleteFilterProps {
  filterName: string;
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string | number | null | undefined, option?: FilterAutocompleteOption | null) => void;
  placeholder?: string;
  freeSolo?: boolean;
  disabled?: boolean;
}

/**
 * Reusable autocomplete filter component with server-side search
 * Fetches options as user types (minimum 2 characters)
 */
export function AutocompleteFilter({
  filterName,
  label,
  value,
  onChange,
  placeholder,
  freeSolo = true,
  disabled = false,
}: AutocompleteFilterProps) {
  const [inputValue, setInputValue] = React.useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState("");

  // Debounce search term to avoid too many API calls
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [inputValue]);

  // When the controlled value is cleared by parent (e.g., Reset Filters),
  // also clear the local input text to visually reset the field.
  React.useEffect(() => {
    if (value == null) {
      setInputValue("");
      setDebouncedSearchTerm("");
    }
  }, [value]);

  const {
    data: options = [],
    isLoading,
    error,
  } = useFilterAutocompleteSearch(filterName, debouncedSearchTerm);

  // Memoize options to prevent unnecessary re-renders
  const memoizedOptions = React.useMemo(() => options, [options]);

  const selectedOption = React.useMemo(
    () =>
      memoizedOptions.find(
        (opt) => opt.value === value || String(opt.value) === String(value),
      ) || null,
    [memoizedOptions, value],
  );

  if (!hasAutocompleteConfig(filterName)) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Autocomplete
        size="small"
        disabled={disabled}
        options={memoizedOptions}
        loading={isLoading}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          // When clearing via the X button, immediately update state
          if (reason === "clear") {
            console.log(
              `[AutocompleteFilter] Clear button clicked for ${filterName}`,
            );
            setInputValue("");
            setDebouncedSearchTerm("");
            // Also trigger onChange(null) to ensure parent component updates
            onChange(null);
          } else {
            setInputValue(newInputValue);
          }
        }}
        onChange={(event, newValue) => {
          console.log(
            `[AutocompleteFilter] onChange triggered for ${filterName}:`,
            { newValue },
          );
          if (newValue) {
            if (typeof newValue === "string") {
              onChange(newValue, null);
            } else {
              onChange(newValue.value, newValue);
            }
          } else {
            // Clear both the filter value and input value when cleared
            console.log(
              `[AutocompleteFilter] Clearing filter ${filterName}, calling onChange(null)`,
            );
            setInputValue("");
            setDebouncedSearchTerm("");
            onChange(null, null);
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option.label || String(option.value);
        }}
        groupBy={(option) => option.group || ""}
        freeSolo={freeSolo}
        clearOnEscape
        isOptionEqualToValue={(option, val) => {
          if (!val) return false;
          if (typeof val === "string" || typeof val === "number") {
            return option.value === val;
          }
          return option.value === val.value;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              placeholder ||
              (debouncedSearchTerm.length < 1
                ? "Type to search..."
                : "Search...")
            }
            variant="outlined"
            error={!!error}
            helperText={error ? "Failed to load options" : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderGroup={(params) => (
          <div key={params.key}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                px: 2,
                py: 1,
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {params.group}
            </Typography>
            {params.children}
          </div>
        )}
        noOptionsText={
          debouncedSearchTerm.length < 1
            ? "Type to search"
            : isLoading
              ? "Loading..."
              : "No options found"
        }
      />
    </Stack>
  );
}
